import queryString from "query-string";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import Card from "src/components/Card";
import FormGroup from "src/components/form/FormGroup";
import Input from "src/components/form/Input";
import Switch from "src/components/form/Switch";
import Stars from "src/components/Stars";
import usePost from "src/hooks/rest/usePost";
import useAuth from "src/hooks/selectors/useAuth";
import useAlert from "src/hooks/useAlert";
import useQueryString from "src/hooks/useQueryString";
import withTranslation, { Translation } from "../hoc/withTranslation";

type ReviewFormProps = {
	taskId?: any;
	userId?: any;
	companyId?: any;
	registrationId?: any;
	onDone: (review: any) => void;
	onCancel?: () => void;
	variant?: "cancel";
	theme?: "card" | "flat";
	hideDescription?: boolean;
};

const ReviewForm = ({
	t,
	taskId,
	userId,
	registrationId,
	companyId,
	onDone,
	onCancel,
	variant,
	theme,
	hideDescription,
}: ReviewFormProps & Translation) => {
	const Wrapper = theme === "flat" ? "div" : Card;
	const location = useLocation();
	let url = location.search;
	let params = queryString.parse(url);
	const isCancel = variant === "cancel" || params?.cancel;
	const auth = useAuth();
	const [sendAlert] = useAlert();
	const [rating, setRating] = useState(0);
	const [description, setDescription] = useState("");
	const [showProfile, setShowProfile] = useState(false);
	const [isLoadingDirect, setIsLoadingDirect] = useState(false);
	const [isLoadingReview, setIsLoadingReview] = useState(false);
	const [createReview] = usePost("/reviews");
	const query = useQueryString();
	const navigate = useNavigate();
	let backUrl = null as any;
	if (query?.back) {
		backUrl = atob(query.back as string);
	}

	const handleSubmitDirect = async () => {
		setIsLoadingDirect(true);
		createReview({
			task_id: taskId,
			user_id: userId,
			company_id: companyId,
			rating: 5,
			description: t("auto-review"),
			show_profile: false,
			showProfile,
		})
			.then((res: any) => {
				if (backUrl && typeof backUrl === "string") {
					navigate(backUrl);
				} else {
					onDone(res?.data || null);
				}
			})
			.finally(() => setIsLoadingDirect(false));
	};

	const handleSubmit = async () => {
		const onConfirm = () => {
			setIsLoadingReview(true);
			createReview({
				task_id: taskId,
				user_id: userId,
				company_id: companyId,
				rating: isCancel ? 0 : rating,
				description,
				showProfile,
			})
				.then((res: any) => {
					if (backUrl && typeof backUrl === "string") {
						navigate(backUrl);
					} else {
						onDone(res?.data || null);
					}
				})
				.finally(() => setIsLoadingReview(false));
		};
		if (rating === 0) {
			sendAlert({
				type: "confirm",
				title: t("confirm.title"),
				text: t("confirm.description"),
				onConfirm,
			});
		} else {
			onConfirm();
		}
	};

	return (
		<Wrapper title={isCancel ? t("title-cancel") : t("title")}>
			{!isCancel && (
				<>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col">
							<p>{t("direct.title")}</p>
							<p className="opacity-60 text-balance">
								{t("direct.description")}
							</p>
						</div>
						<Button
							className="w-max"
							onClick={handleSubmitDirect}
							loading={isLoadingDirect}
						>
							{t("direct.button")}
						</Button>
					</div>
					<hr className="border-border my-4" />
				</>
			)}
			<div className="flex flex-col gap-4">
				{!isCancel && (
					<div className="flex flex-col gap-4">
						<div className="flex flex-col">
							<p>{t("review.title")}</p>
							<p className="opacity-60 text-balance">
								{t("review.description")}
							</p>
						</div>
					</div>
				)}
				{isCancel && (
					<p className="text-balance">{t("cancel.description")}</p>
				)}
				<div className="flex flex-col gap-2">
					{!isCancel && (
						<FormGroup label={t("rating")}>
							<div className="flex flex-col gap-2">
								<Stars
									size="24px"
									inActiveClassName="text-placeholder"
									rating={rating}
									onClick={setRating}
								/>
								<small>{t("score")}</small>
							</div>
						</FormGroup>
					)}

					<Input
						label={t("description")}
						name="description"
						value={description}
						onChange={(_, value) => setDescription(value)}
						valid={description !== ""}
						multiline
					/>

					{isCancel && (
						<Switch
							label={t("show-switch.label")}
							subLabel={t("show-switch.sub-label")}
							value={showProfile}
							onChange={setShowProfile}
						/>
					)}
				</div>
				<div className="flex justify-between gap-4">
					{onCancel && (
						<Button
							onClick={() => {
								if (backUrl && typeof backUrl === "string") {
									navigate(backUrl);
									return;
								}
								onCancel();
							}}
							type="gray"
							className={`gtm-${auth.type}-dashboard-review--button-annuleren`}
						>
							{t("buttons.cancel")}
						</Button>
					)}
					<Button
						loading={isLoadingReview}
						disabled={!description}
						onClick={handleSubmit}
						className={`gtm-${auth.type}-dashboard-review--button-opslaan`}
					>
						{isCancel
							? t("buttons.save-cancel")
							: t("buttons.save")}
					</Button>
				</div>
			</div>
		</Wrapper>
	);
};

ReviewForm.locale = {
	nl: {
		"auto-review": "Automatische review",
		direct: {
			title: "Fijn dat de opdracht goed is verlopen!",
			description:
				"Wanneer je op de onderstaande knop klikt, wordt de opdracht als voltooid gemarkeerd en zal de uitbetaling in gang worden gezet.",
			button: "Samenwerking uitbetalen",
		},
		review: {
			title: "Laat een review achter over de samenwerking",
			description:
				"Geef hieronder aan hoe de samenwerking verlopen is. Dit helpt andere opdrachtgevers om de juiste professional te selecteren. Een review is niet verplicht.",
		},
		title: "Hoe is de opdracht verlopen?",
		"title-cancel": "Vervelend dat de klus niet is geklaard",
		confirm: {
			title: "Weet je het zeker?",
			description:
				"Je staat op het punt om een 0 review te geven, wij beschouwen een 0 review als een samenwerking die geannuleerd is. Het bedrag zal dan teruggestort worden naar je account.",
		},
		cancel: {
			description:
				"Is de professional niet komen opdagen? Of is de opdracht niet juist uitgevoerd? Geef hieronder aan wat er is misgegaan. Een medewerker van {{tenant.name}} neemt dan contact op en regelt de refund van de opdracht.",
		},
		score: "5 = heel goed, 0 = heel slecht",
		description: "Omschrijving",
		rating: "Rating",
		skills: {
			label: "Blinkt de professional uit in sommige vaardigheden?",
			description:
				"Het helpt de professional wanneer je de vaardigheden een like geeft. Je kan een professional maar 1 keer liken",
		},
		"show-switch": {
			label: "Review tonen",
			"sub-label":
				"Wil je deze review tonen op het profiel van de professional",
		},
		buttons: {
			cancel: "Annuleren",
			save: "Samenwerking uitbetalen",
			"save-cancel": "Samenwerking annuleren",
		},
	},
	en: {
		"auto-review": "Automatic review",
		direct: {
			title: "Good that the task went well!",
			description:
				"When you click the button below, the task will be marked as completed and the payment will be initiated.",
			button: "Payout collaboration",
		},
		review: {
			title: "Leave a review about the collaboration",
			description:
				"Indicate below how the collaboration went. This helps other task owners to select the right professional. A review is not required.",
		},
		title: "How did the task go?",
		"title-cancel": "Sorry the task was not completed",
		confirm: {
			title: "Are you sure?",
			description: "You are about to give a 0 rating",
		},
		cancel: {
			description:
				"Did the professional not show up? Or was the task not performed correctly? Indicate below what went wrong. A {{tenant.name}} employee will then contact you and arrange for the task refund.",
		},
		score: "5 = very good, 0 = very bad",
		description: "Description",
		rating: "Rating",
		skills: {
			label: "Does the professional excel in some skills?",
			description:
				"It helps the professional when you give the skills a like. You can only like a professional once.",
		},
		"show-switch": {
			label: "Show Review",
			"sub-label":
				"Do you want to show this review on the professional's profile",
		},
		buttons: {
			cancel: "Cancel",
			save: "Payout collaboration",
			"save-cancel": "Cancel collaboration",
		},
	},
};

export default withTranslation(ReviewForm);
